import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, MaskLines, Reveal } from "./Reveal";
import { IMG, PROCESS, STATS } from "./data";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="bg-[#090B0E] py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal className="clip-corner relative h-[420px] overflow-hidden lg:sticky lg:top-28 lg:h-[620px]">
            <motion.img style={{ y, scale: 1.25 }} src={IMG.about} alt="Bennett project lead reviewing site work" className="absolute inset-0 h-full w-full object-cover grayscale-[30%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090B0E]/80 to-transparent" />
            <p className="absolute bottom-6 left-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white">On site, every day.</p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Eyebrow>About Bennett</Eyebrow>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-6xl lg:text-7xl">
            <MaskLines inView lines={["Builders first.", <span key="b" className="text-stroke">No shortcuts.</span>]} />
          </h2>
          <Reveal>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Bennett Custom Builders is a hands-on, licensed design-build contractor (New Mexico GB98) with 34 years of building experience, serving homeowners and businesses across Northern New Mexico. We run tight sites, communicate clearly and treat your budget like our own — whether it's a family home or a commercial build-out.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
            {STATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 0.1} className="bg-[#090B0E] p-6">
                <p data-testid={`stat-${i}`} className="font-display text-5xl font-black text-blue-500">{s.v}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">{s.l}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-14">
            <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-slate-300">Our process</p>
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div data-testid={`process-step-${i}`} className="group grid grid-cols-[auto_1fr] gap-6 border-t border-white/10 py-7 transition-colors duration-500 hover:bg-white/[0.02] sm:grid-cols-[80px_1fr_1.4fr]">
                  <span className="font-mono text-sm text-blue-400">{p.n}</span>
                  <h3 className="font-display text-2xl font-bold uppercase text-white transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">{p.title}</h3>
                  <p className="col-span-2 text-sm leading-relaxed text-slate-300 sm:col-span-1 sm:text-base">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
